import type { CategoryMeta } from '../context/ClassificationSchemaContext';

// <purpose>THE implication rule for FLAGS (part 7 fix loop, 2026-10-12, operator ruling): a flag is
// hidden only when the visible primary already implies it. Nothing is locked per axis and nothing is
// invented — the answer is computed from served data read through `cm`: the flag landings (`/config`
// `flagLandings`, flag id -> { primary, implied }). One predicate family:
//   isFlagImplied — the row's visible primary IS the flag's served landing, and the landing is
//       declared implied (every row on that primary carries the flag): the band already says it.
// part 7 round 2, 2026-10-13: secondary codes are never hidden — a redundant code is a vocabulary
// defect, shown as served.
// `visibleFlags` is the one composition every flag surface applies (list row normal + compact, the
// Archiv ledger, the detail header; the Begründung draws no flags since R-F12).</purpose>
// <constraints>Pure, no React, no role literal: a role whose payload serves no landing (VNB, WIM
// today) hides nothing, by data. A missing primary (a row not yet classified) implies nothing —
// there is no visible band to stand for anything.
// Hiding is a DISPLAY decision only — a hidden flag still counts in the facet totals and stays
// filterable through the category that implies it (see FilterPanel's „Warnhinweis" section).</constraints>

/** The primary value that implies this flag (its landing, when declared implied), else null. */
export function flagImpliedBy(cm: CategoryMeta, flagId: string): string | null {
  const landing = cm.flagLanding(flagId);
  return landing && landing.implied && landing.primary ? landing.primary : null;
}

/** The visible primary implies the flag. */
export function isFlagImplied(cm: CategoryMeta, flagId: string, primaryId: string | null | undefined): boolean {
  return !!primaryId && flagImpliedBy(cm, flagId) === primaryId;
}

/** The flags a surface still draws beside a visible primary (order preserved). */
export function visibleFlags<T extends string>(cm: CategoryMeta, flags: readonly T[], primaryId: string | null | undefined): T[] {
  return flags.filter(id => !isFlagImplied(cm, id, primaryId));
}
